import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/utils/api';
import { formatGs } from '@/utils/helpers';
import { toast } from 'sonner';

const STORAGE_KEY = 'sushi_notified_orders';
const POLL_INTERVAL = 12000;

function getNotified() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
}
function addNotified(ids) {
  const all = [...new Set([...getNotified(), ...ids])].slice(-200); // keep last 200
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

let audioEl = null;
function playDing() {
  try {
    if (!audioEl) { audioEl = new Audio('/sounds/new-order.wav'); audioEl.volume = 0.7; }
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
  } catch {}
}

function sendDesktopNotification(order, navigate) {
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification('Nuevo pedido - Sushi Express', {
      body: `Pedido #${order.id} - Total ${formatGs(order.total)}`,
      icon: '/icon-192.png',
      tag: `order-${order.id}`,
    });
    n.onclick = () => { window.focus(); navigate(`/admin/pedidos/${order.id}`); n.close(); };
  } catch {}
}

export function useOrderAlerts(navigate) {
  const location = useLocation();
  const lastCheckRef = useRef(null);

  const checkNewOrders = useCallback(async () => {
    try {
      const { data } = await api.get('/pedidos?status=pendiente&limit=20');
      const pendientes = data.orders || [];
      if (pendientes.length === 0) return;

      const notified = getNotified();
      const newOrders = pendientes.filter(o => !notified.includes(o.id));
      if (newOrders.length === 0) return;

      // Skip if user is already viewing one of these orders
      const viewingId = location.pathname.match(/\/admin\/pedidos\/(\d+)/)?.[1];

      addNotified(newOrders.map(o => o.id));
      playDing();

      newOrders.forEach(order => {
        if (String(order.id) === viewingId) return;

        toast(`Nuevo pedido #${order.id}`, {
          description: `${order.customer_name} - ${formatGs(order.total)}`,
          duration: 5000,
          action: { label: 'Ver pedido', onClick: () => navigate(`/admin/pedidos/${order.id}`) },
        });

        sendDesktopNotification(order, navigate);
      });
    } catch {}
  }, [navigate, location.pathname]);

  useEffect(() => {
    // Initial check after small delay
    const init = setTimeout(checkNewOrders, 2000);
    const interval = setInterval(checkNewOrders, POLL_INTERVAL);
    return () => { clearTimeout(init); clearInterval(interval); };
  }, [checkNewOrders]);
}

export function requestNotificationPermission() {
  if (!('Notification' in window)) return Promise.resolve('unsupported');
  return Notification.requestPermission();
}

export function getNotificationStatus() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}
